import { Formik } from 'formik'
import React, { Fragment, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CustomerUploadTSA, CustomerUploadUpdatedTSA } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { CustomerTSAFormValues } from './CustomerTSAForm.interface'
import {
  CustomerOnboardingNotRegisteredAgentTSASchema,
  CustomerOnboardingRegisteredAgentTSASchema,
  CustomerOnboardingTSASchema,
} from './CustomerTSAForm.validator'
import tw from 'twin.macro'
import { showToast } from 'src/utils/toast'
import { Button } from 'src/components/shared/Button'
import { handleApiError } from 'src/utils/errorHandlers'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { FormikRadioGroup } from 'src/components/shared/form/formik/FormikRadioGroup'
import { getNotCompletedCustomerOnboardingCollectDataIdByType } from 'src/selectors/customer'
import { customerComplianceStepTitles } from 'src/constants/customer/constants'
import { CustomerCollectedDataType } from 'src/constants/customer/enums'
import { customerComplianceStepIcons } from 'src/constants/customer/icons'
import { CustomerFormHeader } from '../shared/CustomerFormHeader'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { parsePhoneNumber } from 'libphonenumber-js'

const defaultInitialValues: CustomerTSAFormValues = {
  isRegisteredAgent: '',
  iacNumber: '',
  dateOfLastCertification: '',
  agentName: '',
  coordinatorName: '',
  coordinatorPhoneNumber: '',
  coordinatorEmail: '',
  hasTwic: '',
  twicContractorsNumber: undefined,
  hasFast: '',
  fastContractorsNumber: undefined,
  hasSida: '',
  sidaContractorsNumber: undefined,
  hasHazmat: '',
  hazmatContractorsNumber: undefined,
}

const DATE_MASK = [/\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/]

const radioOptions = [
  {
    label: 'Yes',
    value: 'yes',
  },
  {
    label: 'No',
    value: 'no',
  },
]

interface Props {
  stepNumber?: number
  stepCount?: number
  initialValues?: CustomerTSAFormValues
  onSubmit?: () => void
}

export const CustomerTSAForm = ({ stepNumber, stepCount, initialValues, onSubmit }: Props) => {
  const dispatch = useDispatch()

  const notCompletedCustomerOnboardingCollectDataId = useSelector(
    getNotCompletedCustomerOnboardingCollectDataIdByType(CustomerCollectedDataType.TSA),
  )

  const isResubmit = !!initialValues

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isRegisteredAgent, setIsRegisteredAgent] = useState<string | undefined>(
    initialValues?.isRegisteredAgent,
  )

  const handleFormSubmit = async ({
    isRegisteredAgent,
    iacNumber,
    dateOfLastCertification,
    agentName,
    coordinatorName,
    coordinatorPhoneNumber,
    coordinatorEmail,
    hasTwic,
    twicContractorsNumber,
    hasFast,
    fastContractorsNumber,
    hasSida,
    sidaContractorsNumber,
    hasHazmat,
    hazmatContractorsNumber,
  }: CustomerTSAFormValues) => {
    setIsLoading(true)

    const resFields =
      isRegisteredAgent === 'yes'
        ? {
            iacNumber,
            dateOfLastCertification,
            agentName,
            coordinatorName,
            coordinatorPhoneNumber: coordinatorPhoneNumber
              ? parsePhoneNumber(`${coordinatorPhoneNumber}`, 'US').number
              : undefined,
            coordinatorEmail,
          }
        : {
            hasTwic: hasTwic === 'yes',
            twicContractorsNumber,
            hasFast: hasFast === 'yes',
            fastContractorsNumber,
            hasSida: hasSida === 'yes',
            sidaContractorsNumber,
            hasHazmat: hasHazmat === 'yes',
            hazmatContractorsNumber,
          }

    const res = {
      ...resFields,
      isRegisteredAgent: isRegisteredAgent === 'yes',
      collectedDataId: notCompletedCustomerOnboardingCollectDataId || undefined,
    }

    if (isResubmit) {
      delete res.collectedDataId
    }

    try {
      if (isResubmit) {
        await createAsyncAction(dispatch, CustomerUploadUpdatedTSA.request(res))
      } else {
        await createAsyncAction(dispatch, CustomerUploadTSA.request(res))
      }

      showToast('Business details submitted successfully', {
        variant: 'success',
      })

      if (onSubmit) {
        onSubmit()
      }
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const validationSchema = useMemo(() => {
    if (isRegisteredAgent === 'yes') {
      return CustomerOnboardingRegisteredAgentTSASchema
    }

    if (isRegisteredAgent === 'no') {
      return CustomerOnboardingNotRegisteredAgentTSASchema
    }

    return CustomerOnboardingTSASchema
  }, [isRegisteredAgent])

  return (
    <Formik
      initialValues={initialValues || defaultInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, setFieldValue }) => (
        <div css={tw`w-[318px] bg-white rounded-lg`}>
          <CustomerFormHeader
            title={stepCount ? undefined : 'Resubmit info'}
            stepCount={stepCount}
            stepNumber={stepNumber}
          />
          <div css={tw`px-4 pt-6 py-2`}>
            {customerComplianceStepIcons[CustomerCollectedDataType.TSA]}
            <Typography variant="h4" css={tw`mt-2 mb-2`}>
              {customerComplianceStepTitles[CustomerCollectedDataType.TSA]}
            </Typography>
            <div css={tw`w-full h-[1px] bg-[#EDEDED]`} />
            <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-4 mt-2`}>
              To ensure compliance with TSA security protocols, please answer the following
              questions about your company and drivers.
            </Typography>
            <div css={tw`w-full mb-4`}>
              <FormikRadioGroup
                name="isRegisteredAgent"
                label="Are you a registered Agent and a Certified IAC (Indirect Air Carrier) established in VeRoot with TSA certified drivers?*"
                options={radioOptions}
                groupDataTestId="customer-account-onboarding-tsa-registered-agent-radio-group"
                optionDataTestIdPrefix="customer-account-onboarding-tsa-registered-agent-option"
                onChange={(e) => {
                  setIsRegisteredAgent(e.target.value)
                  setFieldValue('isRegisteredAgent', e.target.value)
                }}
              />
            </div>

            {isRegisteredAgent === 'yes' && (
              <Fragment>
                <Typography variant="h6" css={tw`mt-6 mb-2`}>
                  General Information
                </Typography>
                <div css={tw`w-full h-[1px] bg-[#EDEDED] mb-4`} />
                <FormikTextField
                  name="iacNumber"
                  css={tw`w-full mb-4`}
                  label="IAC Number*"
                  placeholder="Enter IAC number"
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-iac-number-input',
                  }}
                />
                <FormikTextField
                  name="dateOfLastCertification"
                  css={tw`w-full mb-4`}
                  label="Date of Last Certification*"
                  placeholder="YYYY-MM-DD"
                  mask={DATE_MASK}
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-date-last-certification-input',
                  }}
                />
                <FormikTextField
                  name="agentName"
                  css={tw`w-full mb-4`}
                  label="Agent Name as Listed in VeRoot*"
                  placeholder="Enter full name"
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-agent-name-input',
                  }}
                />
                <Typography variant="h6" css={tw`mt-2 mb-2`}>
                  Security Coordinator Contact Info
                </Typography>
                <div css={tw`w-full h-[1px] bg-[#EDEDED] mb-4`} />
                <FormikTextField
                  name="coordinatorName"
                  css={tw`w-full mb-4`}
                  label="Full Name*"
                  placeholder="Enter full name"
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-coordinator-name-input',
                  }}
                />
                <FormikTextField
                  name="coordinatorPhoneNumber"
                  css={tw`w-full mb-4`}
                  label="Phone Number*"
                  placeholder="Enter phone number"
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-coordinator-phone-input',
                  }}
                />
                <FormikTextField
                  name="coordinatorEmail"
                  css={tw`w-full mb-4`}
                  label="Email*"
                  placeholder="Enter email"
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-coordinator-email-input',
                  }}
                />
              </Fragment>
            )}
            {isRegisteredAgent === 'no' && (
              <Fragment>
                <Typography variant="h6" css={tw`mt-6 mb-2`}>
                  Additional Security Credentials
                </Typography>
                <div css={tw`w-full h-[1px] bg-[#EDEDED] mb-2`} />
                <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-4`}>
                  To ensure compliance with TSA security protocols, please answer the following
                  questions about your company and drivers.
                </Typography>
                <div css={tw`w-full mb-4`}>
                  <FormikRadioGroup
                    name="hasTwic"
                    label="Do you have a TWIC (Transportation Worker Identification Credential)?*"
                    options={radioOptions}
                    groupDataTestId="customer-account-onboarding-tsa-has-twic-radio-group"
                    optionDataTestIdPrefix="customer-account-onboarding-tsa-has-twic-option"
                  />
                </div>
                <FormikTextField
                  name="twicContractorsNumber"
                  css={tw`w-full mb-4`}
                  label="Number of your drivers that have a TWIC*"
                  placeholder="Enter number of drivers (e.g. 3)"
                  type="number"
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-twic-drivers-count-input',
                  }}
                />
                <div css={tw`w-full mb-4`}>
                  <FormikRadioGroup
                    name="hasFast"
                    label="Do you have a FAST (Free and Secure Trade) card?*"
                    options={radioOptions}
                    groupDataTestId="customer-account-onboarding-tsa-has-fast-radio-group"
                    optionDataTestIdPrefix="customer-account-onboarding-tsa-has-fast-option"
                  />
                </div>
                <FormikTextField
                  name="fastContractorsNumber"
                  css={tw`w-full mb-4`}
                  label="Number of your drivers that have a FAST card*"
                  placeholder="Enter number of drivers (e.g. 3)"
                  type="number"
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-fast-drivers-count-input',
                  }}
                />
                <div css={tw`w-full mb-4`}>
                  <FormikRadioGroup
                    name="hasSida"
                    label="Do you have a SIDA (Security Identification Display Area) card?*"
                    options={radioOptions}
                    groupDataTestId="customer-account-onboarding-tsa-has-sida-radio-group"
                    optionDataTestIdPrefix="customer-account-onboarding-tsa-has-sida-option"
                  />
                </div>
                <FormikTextField
                  name="sidaContractorsNumber"
                  css={tw`w-full mb-4`}
                  label="Number of your drivers that have a SIDA card*"
                  placeholder="Enter number of drivers (e.g. 3)"
                  type="number"
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-sida-drivers-count-input',
                  }}
                />
                <div css={tw`w-full mb-4`}>
                  <FormikRadioGroup
                    name="hasHazmat"
                    label="Do you have a CDL with HAZMAT endorsement?*"
                    options={radioOptions}
                    groupDataTestId="customer-account-onboarding-tsa-has-hazmat-radio-group"
                    optionDataTestIdPrefix="customer-account-onboarding-tsa-has-hazmat-option"
                  />
                </div>
                <FormikTextField
                  name="hazmatContractorsNumber"
                  css={tw`w-full mb-4`}
                  label="Number of your drivers that have a CDL with HAZMAT endorsement*"
                  placeholder="Enter number of drivers (e.g. 3)"
                  type="number"
                  inputProps={{
                    'data-testid': 'customer-account-onboarding-tsa-hazmat-drivers-count-input',
                  }}
                />
              </Fragment>
            )}
          </div>
          <div css={tw`px-4 py-3`}>
            <Button
              data-testid="customer-account-onboarding-tsa-submit-btn"
              variant="contained"
              size="small"
              loading={isLoading}
              className={tw`w-full`}
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
    </Formik>
  )
}
